import { GroupChannel, Member } from '@sendbird/chat/groupChannel';

import {
  CHANNEL_CLASSIFICATION,
  ChannelTypes,
  MENTOR_ROLE_EVOLUTION_CHANNEL_NAMES,
  NAME_CONNECTOR,
  SUPPORT_TAG,
  UserRoles,
} from '~/lib/constants/chat.constants';
import { ChatUser, MemberMetaData } from '~/lib/types/chat';
import { isExternalChannel } from './chat.utils';

export enum ChatChannelTab {
  INTERNAL,
  EXTERNAL,
}

export enum ChatChannelSelectType {
  MYCHANNELS = 'My channels',
  OTHERCHANNELS = 'Other channels',
}

export const getMembersOfRole = (
  channelMembers: Member[],
  role: UserRoles,
): ChatUser[] => {
  if (channelMembers && role) {
    return [
      ...new Set(
        channelMembers
          .map((member) => {
            const memberMetaData = member.metaData as MemberMetaData;

            if (memberMetaData.userType && memberMetaData.userType === role) {
              const { metaData, nickname, userId } = member;
              return { metaData, nickname, userId } as ChatUser;
            }

            return null;
          })
          .filter((x) => x),
      ),
    ];
  }

  return [];
};

export const getChannelType = (channel: GroupChannel): ChannelTypes => {
  if (channel) {
    return (
      CHANNEL_CLASSIFICATION.find((value) =>
        (channel?.url ?? '').startsWith(value.url),
      )?.type ?? ChannelTypes.UNKNOWN
    );
  }

  return ChannelTypes.UNKNOWN;
};

export const getUserFirstName = (nickname: string): string => {
  if (nickname) {
    return nickname.split(' ')[0];
  }

  return '';
};

export const getFirstNames = (users: ChatUser[]): string => {
  return (users ?? [])
    .map((user) => getUserFirstName(user.nickname))
    .join(NAME_CONNECTOR);
};

export const getMentorRoleEvolutionChannelType = (
  channelType: ChannelTypes,
): ChannelTypes => {
  return MENTOR_ROLE_EVOLUTION_CHANNEL_NAMES[channelType] ?? channelType;
};

export const getChannelName = (channel: GroupChannel): string => {
  const channelType = getChannelType(channel);

  const evolvedChannelType = getMentorRoleEvolutionChannelType(channelType);

  const support = getMembersOfRole(channel?.members, UserRoles.SUPPORT);

  const supportFirstName = getUserFirstName(support?.[0]?.nickname);

  const isExternal = isExternalChannel(channel?.url);

  switch (evolvedChannelType) {
    case ChannelTypes.SUPPORT_AND_DIETITIANS:
    case ChannelTypes.SUPPORT_AND_FAMILY_MENTORS:
    case ChannelTypes.SUPPORT_AND_CAREGIVER_COACHES:
    case ChannelTypes.SUPPORT_AND_THERAPISTS:
    case ChannelTypes.SUPPORT_AND_MEDICAL_TEAM:
    case ChannelTypes.SUPPORT_AND_PSYCH_TEAM:
      return evolvedChannelType.replace(SUPPORT_TAG, supportFirstName);

    case ChannelTypes.PATIENT_TO_CLINICAL_CARE_TEAM: {
      if (!isExternal) {
        return ChannelTypes.PATIENT_TO_CLINICAL_CARE_TEAM;
      }
      return ChannelTypes.PATIENT_TO_THERAPY_RD_COACHING_TEAM;
    }

    case ChannelTypes.PROXY_TO_CLINICAL_CARE_TEAM: {
      if (!isExternal) {
        return ChannelTypes.PROXY_TO_CLINICAL_CARE_TEAM;
      }

      return ChannelTypes.PROXY_TO_THERAPY_RD_COACHING_TEAM;
    }

    case ChannelTypes.PATIENT_AND_PROXY_TO_CARE_TEAM: {
      if (!isExternal) {
        return ChannelTypes.PATIENT_AND_PROXY_TO_CARE_TEAM;
      }

      return ChannelTypes.PATIENT_AND_PROXY_TO_THERAPY_RD_COACHING_TEAM;
    }

    case ChannelTypes.PATIENT_TO_THERAPY_RD_COACHING_TEAM:
      return ChannelTypes.PATIENT_TO_THERAPY_RD_COACHING_TEAM;

    case ChannelTypes.PROXY_TO_THERAPY_RD_COACHING_TEAM:
      return ChannelTypes.PROXY_TO_THERAPY_RD_COACHING_TEAM;

    case ChannelTypes.PATIENT_AND_PROXY_TO_THERAPY_RD_COACHING_TEAM:
      return ChannelTypes.PATIENT_AND_PROXY_TO_THERAPY_RD_COACHING_TEAM;

    default:
      return evolvedChannelType;
  }
};

export const getProviderDisplayName = (
  providerType?: string,
): string | undefined => {
  switch (providerType) {
    case 'Peer Mentor':
      return 'Recovery Coach';

    case 'Family Mentor':
      return 'Caregiver Coach';

    case 'Recovery Navigator':
      return 'Care Navigator';

    default:
      return providerType;
  }
};
